import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

// API Configuration Constants
const API_CONFIG = {
  DEFAULT_TIMEOUT: 10000,           // 10 seconds - default timeout for most API requests
  TOKEN_REFRESH_TIMEOUT: 30000,     // 30 seconds - timeout for token refresh requests
} as const;

// API Base URL Configuration
const getApiBaseUrl = () => {
  const baseUrl = import.meta.env.VITE_API_BASE_URL;
  if (!baseUrl) {
    throw new Error('VITE_API_BASE_URL environment variable is not set. Please configure your environment properly.');
  }
  // Remove trailing slash from baseUrl to prevent double slashes
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${cleanBaseUrl}/api`;
};

const API_BASE_URL = getApiBaseUrl();

console.log('🔧 API Base URL:', API_BASE_URL);

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-client-platform': 'webplay', // Identify as webplay for server routing decisions
  },
  timeout: API_CONFIG.DEFAULT_TIMEOUT,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('beemi_webplay_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔑 Adding auth token to request:', config.method?.toUpperCase(), config.url);
    }
    return config;
  },
  (error) => {
    console.error('❌ Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => {
    console.log('✅ API Response:', response.status, response.config.method?.toUpperCase(), response.config.url);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    console.error('❌ API Error:', error.response?.status, error.config?.method?.toUpperCase(), error.config?.url);
    
    // Don't trigger token refresh for authentication endpoints
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/google') || 
                          originalRequest?.url?.includes('/auth/apple') || 
                          originalRequest?.url?.includes('/auth/login');
    
    // Handle 401 Unauthorized (expired token) - but not for auth endpoints
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      
      try {
        console.log('🔄 Attempting to refresh token...');
        
        // Get refresh token
        const refreshToken = localStorage.getItem('beemi_webplay_refresh_token');
        const accessToken = localStorage.getItem('beemi_webplay_access_token');
        
        console.log('🔍 Token check:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
        });
        
        if (!refreshToken) {
          console.error('❌ No refresh token available');
          throw new Error('No refresh token');
        }
        
        // Refresh the token
        console.log('📡 Making refresh request to:', `${API_BASE_URL}/auth/refresh`);
        const refreshResponse = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken: refreshToken,
        });
        
        if (refreshResponse.data.success && refreshResponse.data.tokens) {
          console.log('✅ Token refresh successful');
          
          // Store new tokens
          localStorage.setItem('beemi_webplay_access_token', refreshResponse.data.tokens.access_token);
          localStorage.setItem('beemi_webplay_refresh_token', refreshResponse.data.tokens.refresh_token);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.tokens.access_token}`;
          return api(originalRequest);
        } else {
          console.error('❌ Refresh response invalid:', refreshResponse.data);
          throw new Error('Invalid refresh response');
        }
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        
        // Clear tokens and redirect to login
        localStorage.removeItem('beemi_webplay_access_token');
        localStorage.removeItem('beemi_webplay_refresh_token');
        
        // Redirect to login page only if not already there
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    
    // Handle connection errors
    if (error.code === 'ECONNABORTED' || error.message === 'Request aborted' || error.code === 'NETWORK_ERROR') {
      if (error.code === 'ECONNABORTED' && error.message?.includes('timeout')) {
        console.error('⏱️ Request timeout - server took too long to respond');
        const timeoutError = new Error('Request timeout. Please try again.');
        timeoutError.name = 'TimeoutError';
        return Promise.reject(timeoutError);
      }
      
      console.error('🔌 Server connection failed');
      const connectionError = new Error(`Unable to connect to server at ${API_BASE_URL}. Please check your network connection.`);
      connectionError.name = 'ConnectionError';
      return Promise.reject(connectionError);
    }
    
    return Promise.reject(error);
  }
);

// Helper function to create API methods
export const apiService = {
  get: <T = any>(url: string, config?: AxiosRequestConfig) => {
    console.log('📡 GET request to:', url);
    return api.get<T>(url, config);
  },
  
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    console.log('📡 POST request to:', url);
    return api.post<T>(url, data, config);
  },
  
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    console.log('📡 PUT request to:', url);
    return api.put<T>(url, data, config);
  },
  
  delete: <T = any>(url: string, config?: AxiosRequestConfig) => {
    console.log('📡 DELETE request to:', url);
    return api.delete<T>(url, config);
  },
  
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig) => {
    console.log('📡 PATCH request to:', url);
    return api.patch<T>(url, data, config);
  },
};

export default api;

